<template>
  <PassengerCard
    :pax-type="passengerType"
    source-page="passengersData"
    :is-invalid="validations.$error"
    :passenger="passenger"
    :placeholder-data="placeholderData"
    @onActionButtonClick="onSavedPassengersSheetOpen"
  >
    <template #passengerData="{ passengerText }">
      <p class="text-text-neutral-primary truncate font-semibold">
        {{ passengerText?.title }}
      </p>
      <span class="text-text-neutral-tertiary block truncate text-sm">
        {{ passengerText?.description }}
      </span>
    </template>
  </PassengerCard>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { isEqual } from 'lodash-es';
  import { useVuelidate } from '@vuelidate/core';
  import { PassengerCard } from 'freedom-shared';
  import type { Airflow } from '@aviata/platform-types';
  import type { Passenger, PassengerKey } from '@aviata/booking-data';
  import { passengerValidation } from 'shared';

  type PassengerWithDocType = Passenger & {
    id?: string;
    fin?: string;
    document: Passenger['document'] & {
      /**
       * When saved passenger is selected it may have the document type property,
       * which we need to display the passenger's doc type text
       */
      type?: string;
      givenAt?: string;
      series?: string;
      organization?: string;
    };
  };

  const props = defineProps<{
    isDomestic: boolean;
    passenger: PassengerWithDocType;
    passengerKey: PassengerKey;
    paxIdx: number;
    schema: Airflow.Offer.Schema.PassengerEntity;
    savedPassengers: Passenger[];
    providerClass?: string;
    departureDate?: string;
  }>();

  const emit = defineEmits<{
    onPassengerUpdate: [
      updatedPassenger: { key: PassengerKey; passenger: Passenger },
    ];
    onOpenSavedPassengersSheet: [selectedPassenger: Passenger | null];
  }>();

  const passengerType = computed(() => props.passengerKey.slice(0, 3));

  const validationRules = passengerValidation.lib.initValidation({
    schema: props.schema,
    passengerKey: props.passengerKey,
    validationMessagePath: 'passengersData.validations',
    citizenship: computed(() => props.passenger.citizenship),
    departureDate: computed(() => props.departureDate),
    providerClass: props.providerClass,
  });

  /**
   * Need to use $registerAs to reference this validation from
   * a parent component to count valid passengers amount
   * @see https://vuelidate-next.netlify.app/api/configuration.html#registeras
   */
  const validations = useVuelidate(
    validationRules,
    computed(() => props.passenger),
    { $registerAs: props.passengerKey },
  );

  const placeholderData = computed(() => ({
    key: props.passengerKey,
    ageBetween: props.schema.validators.age_between,
  }));

  const onSavedPassengersSheetOpen = () => {
    emit('onOpenSavedPassengersSheet', props.passenger);
  };

  watch(
    () => props.savedPassengers,
    newSavedPassengers => {
      const currentPassengerNumber = props.passenger.document?.number;
      if (!currentPassengerNumber) return;

      const foundPassenger = newSavedPassengers.find(
        p => p.document.number === currentPassengerNumber,
      );

      if (foundPassenger && !isEqual(foundPassenger, props.passenger)) {
        emit('onPassengerUpdate', {
          key: props.passengerKey,
          passenger: foundPassenger,
        });
      }
    },
  );
</script>
